import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useDeleteEmailConfig,
    useGetEmailPreview,
    useGetTournamentConfig,
    useSaveEmailConfig,
    useSendPreviewEmail,
} from '../hooks/tournamentHooks';
import { X } from 'lucide-react';
import { useNotification } from '@iziping/ui';
import type { EmailConfig } from '@iziping/common';
import Toggle from '../../../components/shared/Toggle';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import { SUPPORTED_LANGS } from '../../../config/i18n/i18n';
import formStyles from '../../../styles/form.module.css';
import styles from './TournamentEmailConfig.module.css';

interface Props {
    tournamentId: number;
}

const LANG_FLAGS: Record<string, string> = { fr: '🇫🇷', en: '🇬🇧', nl: '🇳🇱' };

const emptyTranslations = () => Object.fromEntries(SUPPORTED_LANGS.map((l) => [l, '']));

const EMPTY_CONFIG: EmailConfig = {
    enabled: false,
    title: emptyTranslations(),
    thanks: emptyTranslations(),
    bye: emptyTranslations(),
};

const TournamentEmailConfig: React.FC<Props> = ({ tournamentId }) => {
    const { t } = useTranslation();
    const { notify } = useNotification();
    const { data, isLoading } = useGetTournamentConfig(tournamentId);
    const save = useSaveEmailConfig(tournamentId);
    const deleteConfig = useDeleteEmailConfig(tournamentId);

    const preview = useGetEmailPreview(tournamentId);
    const sendPreview = useSendPreviewEmail(tournamentId);

    const [form, setForm] = useState<EmailConfig>(EMPTY_CONFIG);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [activeLang, setActiveLang] = useState<string>(SUPPORTED_LANGS[0]);
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewLang, setPreviewLang] = useState<string>(SUPPORTED_LANGS[0]);

    useEffect(() => {
        if (data) {
            const email = data.emailConfig;
            const hasContent = email && Object.values(email.title ?? {}).some(Boolean);
            setForm({
                title: { ...emptyTranslations(), ...(email?.title ?? {}) },
                thanks: { ...emptyTranslations(), ...(email?.thanks ?? {}) },
                bye: { ...emptyTranslations(), ...(email?.bye ?? {}) },
                enabled: data.emailEnabled || !!hasContent,
            });
        }
    }, [data]);

    const handleToggle = (enabled: boolean) => {
        if (!enabled && form.enabled) {
            setShowDisableModal(true);
        } else {
            setForm((f) => ({ ...f, enabled }));
        }
    };

    const handleConfirmDisable = async () => {
        try {
            await deleteConfig.mutateAsync();
            setForm(EMPTY_CONFIG);
            notify(t('tournament.email.delete.success'), 'success');
        } catch (error: any) {
            notify(t(error.error) || t('internal.server.error'), 'error');
        } finally {
            setShowDisableModal(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await save.mutateAsync(form);
            notify(t('tournament.email.save.success'), 'success');
        } catch (error: any) {
            notify(t(error.error) || t('internal.server.error'), 'error');
        }
    };

    const setField = (field: keyof Pick<EmailConfig, 'title' | 'thanks' | 'bye'>, lang: string, value: string) => {
        setForm((f) => ({ ...f, [field]: { ...f[field], [lang]: value } }));
    };

    const handleOpenPreview = async (lang: string) => {
        setPreviewLang(lang);
        const html = await preview.mutateAsync(lang);
        setPreviewHtml(html);
        setShowPreview(true);
    };

    const handlePreviewLangChange = async (lang: string) => {
        setPreviewLang(lang);
        const html = await preview.mutateAsync(lang);
        setPreviewHtml(html);
    };

    if (isLoading) return <div>Loading...</div>;

    return (
        <div className={styles.wrapper}>
            <div>
                <h3>{t('tournament.email.section.title')}</h3>
                <p className={styles.description}>{t('tournament.email.section.description')}</p>
            </div>

            <Toggle
                checked={form.enabled}
                onChange={handleToggle}
                label={form.enabled ? t('tournament.email.enabled') : t('tournament.email.disabled')}
            />

            <ConfirmationModal
                isOpen={showDisableModal}
                title={t('tournament.email.disable.confirm.title')}
                message={t('tournament.email.disable.confirm.message')}
                confirmLabel={t('tournament.email.disable.confirm.button')}
                onConfirm={handleConfirmDisable}
                onCancel={() => setShowDisableModal(false)}
            />

            {form.enabled && (
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.langTabs}>
                        {SUPPORTED_LANGS.map((lang) => (
                            <button
                                key={lang}
                                type="button"
                                onClick={() => setActiveLang(lang)}
                                className={`${styles.langTab} ${activeLang === lang ? styles.langTabActive : ''}`}
                            >
                                {LANG_FLAGS[lang] ?? lang} {lang.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    <div className={formStyles.formGroup}>
                        <label className={formStyles.label}>{t('tournament.email.title.label')}</label>
                        <input
                            className={formStyles.input}
                            type="text"
                            value={form.title[activeLang] ?? ''}
                            onChange={(e) => setField('title', activeLang, e.target.value)}
                            placeholder={t('tournament.email.title.placeholder')}
                        />
                    </div>

                    <div className={formStyles.formGroup}>
                        <label className={formStyles.label}>{t('tournament.email.thanks.label')}</label>
                        <textarea
                            className={formStyles.input}
                            rows={4}
                            value={form.thanks[activeLang] ?? ''}
                            onChange={(e) => setField('thanks', activeLang, e.target.value)}
                            placeholder={t('tournament.email.thanks.placeholder')}
                        />
                    </div>

                    <div className={formStyles.formGroup}>
                        <label className={formStyles.label}>{t('tournament.email.bye.label')}</label>
                        <textarea
                            className={formStyles.input}
                            rows={3}
                            value={form.bye[activeLang] ?? ''}
                            onChange={(e) => setField('bye', activeLang, e.target.value)}
                            placeholder={t('tournament.email.bye.placeholder')}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button type="submit" className="button" disabled={save.isPending}>
                            {save.isPending ? '...' : t('tournament.email.save.button')}
                        </button>
                        <button
                            type="button"
                            className="button"
                            disabled={preview.isPending}
                            onClick={() => handleOpenPreview(activeLang)}
                        >
                            {preview.isPending ? '...' : t('tournament.email.preview.button')}
                        </button>
                    </div>
                </form>
            )}

            {showPreview && (
                <div className={styles.previewOverlay} onClick={() => setShowPreview(false)}>
                    <div className={styles.previewModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.previewHeader}>
                            <p className={styles.previewTitle}>{t('tournament.email.preview.title')}</p>
                            <button className={styles.previewClose} onClick={() => setShowPreview(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className={styles.previewLangTabs}>
                            {SUPPORTED_LANGS.map((lang) => (
                                <button
                                    key={lang}
                                    type="button"
                                    onClick={() => handlePreviewLangChange(lang)}
                                    className={`${styles.langTab} ${previewLang === lang ? styles.langTabActive : ''}`}
                                >
                                    {LANG_FLAGS[lang] ?? lang} {lang.toUpperCase()}
                                </button>
                            ))}
                        </div>
                        <div className={styles.previewIframeWrapper}>
                            {preview.isPending ? (
                                <p>...</p>
                            ) : (
                                <iframe className={styles.previewIframe} srcDoc={previewHtml} title="email-preview" />
                            )}
                        </div>
                        <div className={styles.previewFooter}>
                            <button
                                type="button"
                                className="button"
                                disabled={sendPreview.isPending}
                                onClick={async () => {
                                    await sendPreview.mutateAsync(previewLang);
                                    notify(t('tournament.email.preview.send.success'), 'success');
                                }}
                            >
                                {sendPreview.isPending ? '...' : t('tournament.email.preview.send.button')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TournamentEmailConfig;
